import * as Sentry from "@sentry/browser"

const IGNORE_ERROR_CODES = [400, 401, 403, 404, 418, 422, 429]
const IGNORED_EXCEPTION_MESSAGE = ["Request aborted"]
const IGNORED_ERROR_MESSAGES = [
    "Network Error",
    "Network error",
    "timeout of",
    "ECONNABORTED",
    "ENOTFOUND",
    "ECONNREFUSED",
    "ERR_NETWORK",
]

export const initSentry = ({ dsn, user }: { dsn: string; user?: Sentry.User }) => {
    Sentry.init({
        dsn,
        environment: window.ENVIRONMENT,
        release: window.CURRENT_VERSION,
        debug: window.ENVIRONMENT === "Development",
        enabled: window.ENVIRONMENT !== "Development",
        beforeSend(event) {
            // Filter out errors with ignored HTTP status codes
            // Check multiple possible locations where status code might be stored
            const exceptionValue = event.exception?.values?.[0]?.value as any
            const errorMessage = (exceptionValue?.message as string) || String(exceptionValue)

            const statusCode =
                exceptionValue?.response?.status ||
                (exceptionValue && parseInt(String(exceptionValue).match(/status code (\d+)/)?.[1], 10))

            // Filter out abort errors - these occur when users navigate away from a page before
            // API requests complete (e.g., user clicks a link while campaign/event data is still loading).
            // These are expected, normal behavior and not actual errors that need to be fixed.
            if (IGNORED_EXCEPTION_MESSAGE.includes(exceptionValue)) return null

            // Filter out network errors by message - catches "Network Error", timeouts, connection errors, etc.
            // These are transient failures on mobile connections and not actual backend errors
            if (IGNORED_ERROR_MESSAGES.some((msg) => errorMessage.includes(msg))) return null

            // Filter out network errors (no response object) - these are transient failures on mobile
            // connections and not actual backend errors that need to be fixed
            if (exceptionValue?.response === undefined || exceptionValue?.response === null) return null

            return statusCode && IGNORE_ERROR_CODES.includes(statusCode) ? null : event
        },
    })

    if (user) {
        Sentry.setUser(user)
    }

    // Add global handler for unhandled promise rejections (e.g., from djangio/action-creators.js)
    // These bypass BACKENDERROR and shouldThrowRavenError, so we need to catch them here
    window.addEventListener("unhandledrejection", (event) => {
        const error = event.reason
        const errorMessage = (error?.message as string) || String(error)

        // Filter out network errors from unhandled rejections
        if (IGNORED_ERROR_MESSAGES.some((msg) => errorMessage.includes(msg))) {
            event.preventDefault() // Prevent Sentry from capturing this
            return
        }

        // Filter out errors without response object (network errors)
        if (error?.response === undefined || error?.response === null) {
            event.preventDefault() // Prevent Sentry from capturing this
            return
        }
    })

    // Remap the legacy SDK to the new one
    // @ts-expect-error We replaced Raven with Sentry, no point in typing dead code
    window.Raven = Sentry
}
